import { pushMessage } from './MessageBusUtils'
import _ from 'lodash'
import { getInvestibleName } from '../contexts/InvestibesContext/investiblesContextHelper'
import { getMarket } from '../contexts/MarketsContext/marketsContextHelper'
import { marketsContextHack } from '../contexts/MarketsContext/MarketsContext';
import { attachmentPathHack, investibleContextHack } from '../contexts/InvestibesContext/InvestiblesContext';
import { getComment, getCommentRoot } from '../contexts/CommentsContext/commentsContextHelper';
import { commentsContextHack } from '../contexts/CommentsContext/CommentsContext'
import { JOB_WIZARD_TYPE } from '../constants/markets';
import { ticketContextHack } from '../contexts/TicketContext/TicketIndexContext';
import {
  getTicket,
  isInvestibleTicket,
  isOptionTicket,
  isTicketPath
} from '../contexts/TicketContext/ticketIndexContextHelper';
import { getInboxTarget, getMessageId } from '../contexts/NotificationsContext/notificationsContextHelper';
import { nameFromDescription } from './stringFunctions';

export const VISIT_CHANNEL = 'VisitChannel';
export const VIEW_EVENT = 'pageView';
export const MARKET_TODOS_HASH = 'marketTodos';
export const DISCUSSION_HASH = 'discussionSection';
export const BACKLOG_HASH = 'insideBacklogSection';
// TODO B-all-396 Make tab ids not depend on tab title
export const ASSIGNED_HASH = 'JobProgress';
const OUR_CND_DOMAIN_ENDING = 'imagecdn.uclusion.com';

/** Given the pathpart _without the hash or query params
 * will extract the action, the marketId and the investibleId
 * Assumes the pathpart has a leading /
 * @param path
 * @return {null}
 */
export function decomposeMarketPath(path) {
  const split = path.split('/');
  // first match is empty. because it's the leading / the action
  // so well have ["", <action>, <marketid>, <investibleid>] in that order
  const [, action, marketId, investibleId] = split;
  return { action, marketId, investibleId };
}

let jobBackOrigin;
let lastSeenNavigationUrl;
let linkSourceAnchor;
let notificationEntryId = 0;
// A comment (c<id>) or an approval (cv<user id>); the job page opens the tab holding either.
const LINK_SOURCE_ANCHOR_RE = /^cv?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * B-all-680: a link that stays on its job leaves no other page to return to, so remember the
 * comment or approval it was clicked in. The next navigate makes that the job's Back origin.
 */
export function rememberLinkSource(element) {
  linkSourceAnchor = undefined;
  for (let node = element; node; node = node.parentElement) {
    if (LINK_SOURCE_ANCHOR_RE.test(node.id || '')) {
      linkSourceAnchor = node.id;
      return;
    }
  }
}

export function getJobBackOrigin() {
  return jobBackOrigin || lastSeenNavigationUrl;
}

export function clearJobBackOrigin() {
  jobBackOrigin = undefined;
}

export function clearNavigationOrigins() {
  jobBackOrigin = undefined;
  lastSeenNavigationUrl = undefined;
}

export function rememberSeenNavigationUrl(url) {
  if (isReturnableNavigationUrl(url)) {
    lastSeenNavigationUrl = url;
  }
}

// T-all-2492: these are flows, not places. A wizard keeps its whole identity in its hash, so a
// remembered one is a bare `/wizard` that renders the shell and nothing else. `/demo` and an
// invite are one shot loaders that fetch or join a market when they render, so returning to one
// re-enters the flow instead of a page. None of them ever goes on the Back stack.
const ONE_SHOT_ROUTES = ['/wizard', '/demo', '/invite'];

export function isReturnableNavigationUrl(url) {
  if (!url) {
    return false;
  }
  const [pathname] = url.split(/[?#]/);
  // Ticket URLs only redirect to a page; remembering one makes Back reopen its destination.
  return !isTicketPath(pathname) &&
    !ONE_SHOT_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

function navigationKey(pathname, search) {
  const { action, marketId, investibleId } = decomposeMarketPath(pathname || '');
  if (action === 'dialog' && marketId && investibleId) {
    return `/dialog/${marketId}/${investibleId}`;
  }
  if (action === 'dialog' && marketId) {
    const groupId = new URLSearchParams(search || '').get('groupId');
    if (groupId) {
      return `/dialog/${marketId}?groupId=${groupId}`;
    }
  }
  // T-all-2492: every other route keeps its query, because that is where some of them carry the
  // thing that makes the page render at all. `/groupEdit/<marketId>` and `/groupManage/<marketId>`
  // draw an empty body without their groupId. Only dialog is canonicalized, so its keys still
  // match formInvestibleLink and formMarketLink for the existence checks that read them.
  if (action !== 'dialog' && search) {
    return `${pathname}${search}`;
  }
  return pathname;
}

export function getCanonicalNavigationUrl(pathname, search) {
  return navigationKey(pathname, search);
}

function rememberOriginIfEnteringJob(fromPathname, fromSearch, to) {
  const sourceAnchor = linkSourceAnchor;
  linkSourceAnchor = undefined;
  if (!to || typeof to !== 'string' || to.startsWith('http')) {
    return;
  }
  const [noHash, toHash] = to.split('#');
  const queryAt = noHash.indexOf('?');
  const toPathname = queryAt === -1 ? noHash : noHash.substring(0, queryAt);
  const toSearch = queryAt === -1 ? '' : noHash.substring(queryAt);
  const { action, investibleId } = decomposeMarketPath(toPathname);
  if (action !== 'dialog' || !investibleId) {
    return;
  }
  const fromUrl = navigationKey(fromPathname, fromSearch);
  const toUrl = navigationKey(toPathname, toSearch);
  if (!isReturnableNavigationUrl(fromUrl)) {
    return;
  }
  if (fromUrl !== toUrl) {
    jobBackOrigin = fromUrl;
  } else if (sourceAnchor && toHash !== sourceAnchor) {
    jobBackOrigin = `${fromUrl}#${sourceAnchor}`;
  }
}

export function broadcastView(marketId, investibleId, isEntry, action, to) {
  const message = { marketId, investibleId, isEntry, action, to };
  pushMessage(
    VISIT_CHANNEL,
    {
      event: VIEW_EVENT,
      message,
    },
  );
}

export function preventDefaultAndProp(event) {
  if (event) {
    event.stopPropagation();
    event.preventDefault();
  }
}

function hasExplicitScrollTarget(to) {
  if (typeof to !== 'string') {
    return false;
  }
  const [pathAndQuery, hashFragment] = to.split('#');
  if (!hashFragment || [ASSIGNED_HASH, BACKLOG_HASH, DISCUSSION_HASH].includes(hashFragment)) {
    return false;
  }
  const [pathname] = pathAndQuery.split('?');
  const { action } = decomposeMarketPath(pathname);
  return pathname === '/' || ['dialog', 'inbox', 'comment'].includes(action);
}

function resetPageScrollAfterNavigation(to) {
  if (hasExplicitScrollTarget(to)) {
    return;
  }
  window.setTimeout(() => {
    window.scrollTo(0, 0);
    document.querySelectorAll('[data-page-scroll-container="true"]').forEach((element) => {
      element.scrollTop = 0;
    });
  }, 0);
}

export function navigate(history, to, insideUseEffect, doNotAddToHistory, state) {
  const navigationState = state?.notification ? {
    ...state, notification: { ...state.notification, entryId: `${Date.now()}-${++notificationEntryId}` }
  } : state;
  const {
    action: fromAction,
    marketId: fromMarketId,
    investibleId: fromInvestibleId,
  } = decomposeMarketPath(history.location.pathname);
  rememberOriginIfEnteringJob(history.location.pathname, history.location.search, to);
  broadcastView(fromMarketId, fromInvestibleId, false, fromAction);
  if (to) {
    if (insideUseEffect) {
      // Without the set timeout the use effect can be re-run before the push is complete
      // though not clear why that run wouldn't run it again.
      setTimeout(() => {
        history.push(to, navigationState);
        resetPageScrollAfterNavigation(to);
      }, 0);
    } else {
      if (doNotAddToHistory) {
        history.replace(to, navigationState);
      } else {
        history.push(to, navigationState);
      }
      resetPageScrollAfterNavigation(to);
    }
  } else {
    if (insideUseEffect) {
      setTimeout(() => {
        history.goBack();
      }, 1);
    } else {
      history.goBack();
    }
  }
  const {
    action: toAction,
    marketId: toMarketId,
    investibleId: toInvestibleId,
  } = decomposeMarketPath(history.location.pathname);
  broadcastView(toMarketId, toInvestibleId, true, toAction, to);
}

export function formInboxItemLinkFromId(id) {
  if (!id.includes('_')) {
    return `/outbox/${id}`;
  }
  return `${getInboxTarget()}/${id}`;
}

export function formInboxItemLink(message) {
  return `${getInboxTarget(message)}/${getMessageId(message)}`;
}

export function formInviteLink(marketToken) {
  const current = window.location.href;
  const url = new URL(current);
  url.pathname = `invite/${marketToken}`;
  url.search = '';
  url.hash = '';
  return url.toString();
}

function getNameForComment(comment, investibleState) {
  const readableTicketCode = decodeURI(comment.ticket_code);
  if (comment.investible_id) {
    const investibleName = getInvestibleName(investibleState, comment.investible_id);
    return `${investibleName} - ${readableTicketCode}`;
  }
  // Don't use name from description as that's not a real name
  return readableTicketCode;
}

export function getUrlForTicketPath(pathname, ticketState, marketsState, commentsState) {
  // The ticket index is keyed by `${marketId}/${ticketCode}`, matching the canonical short-code
  // URL `/${marketId}/Q-all-156`. A normal in-app URL carries a leading `/dialog` action
  // (e.g. `/dialog/${marketId}/Q-all-156`), so strip that prefix before looking the ticket up -
  // that lets a short code pasted into a dialog URL resolve the same way (T-all-2214).
  const ticketPathname = pathname.startsWith('/dialog/') ? pathname.substring('/dialog'.length) : pathname;
  const ticket = getTicket(ticketState, ticketPathname.substring(1));
  if (ticket) {
    if (isInvestibleTicket(pathname)) {
      const { marketId, investibleId } = ticket;
      if (isOptionTicket(pathname)) {
        const market = getMarket(marketsState, marketId) || {};
        const { parent_comment_id: aParentCommentId, parent_comment_market_id: aParentMarketId } = market;
        const parentComment = getComment(commentsState, aParentMarketId, aParentCommentId) || {};
        const { investible_id: parentInvestibleId, group_id: parentGroupId } = parentComment;
        if (parentGroupId) {
          if (parentInvestibleId) {
            return `${formInvestibleLink(aParentMarketId, parentInvestibleId)}#option${investibleId}`;
          }
          return `${formMarketLink(aParentMarketId, parentGroupId)}#option${investibleId}`;
        }
      } else {
        return formInvestibleLink(marketId, investibleId);
      }
    } else {
      const { marketId, commentId, groupId, investibleId } = ticket;
      return formCommentLink(marketId, groupId, investibleId, commentId);
    }
  }
  return undefined;
}

export function getTicketRedirectUrl(pathname, hash, ticketState, marketsState, commentsState) {
  const url = getUrlForTicketPath(pathname, ticketState, marketsState, commentsState);
  if (!url) {
    return url;
  }
  // An option notification rides on its parent question's canonical URL (Q-all-341 O-1):
  // land on the option itself rather than the question's own comment anchor.
  if (hash?.startsWith('#option')) {
    return `${url.split('#')[0]}${hash}`;
  }
  // A canonical vote notification resolves to the job's dialog URL; retain its approvals anchor.
  // Comment and option redirects already contain their own target anchor and must not gain a second one.
  if (!hash?.startsWith('#cv') || url.includes('#')) {
    return url;
  }
  return `${url}${hash}`;
}

/**
 * Resolve a bare short code (e.g. "Q-all-156") - as typed into search, with no marketId - to its
 * canonical URL. The ticket index is keyed by `${marketId}/${ticketCode}`, so find the entry whose
 * last segment matches and reuse getUrlForTicketPath to build the URL (handles jobs and
 * comments alike). Restores short-code search (T-all-2214).
 */
export function getUrlForTicketCode(code, ticketState, marketsState, commentsState) {
  const trimmedRaw   = code?.trim();
  if (_.isEmpty(trimmedRaw)) {
    return undefined;
  }
  const trimmed = trimmedRaw.toUpperCase();
  if (isOptionTicket(`/${trimmed}`)) {
    // Options are not supported in this context because they are in a different market
    return undefined;
  }
  // Match case-insensitively: ticket keys keep the code's original casing (e.g. `${marketId}/J-all-247`)
  // while `trimmed` is uppercased, so compare both sides uppercased or a mixed-case market segment
  // (like "all") never matches and the code wrongly falls through to text search (T-all-2234).
  const key = Object.keys(ticketState || {}).find((aKey) => {
    const upperKey = aKey.toUpperCase();
    return upperKey === trimmed || upperKey.endsWith(`/${trimmed}`) ||
      decodeURI(aKey).toUpperCase().endsWith(`/${trimmed}`);
  });
  if (!key) {
    return undefined;
  }
  return getUrlForTicketPath(`/${key}`, ticketState, marketsState, commentsState);
}

export function getNameForUrl(url) {
  const marketState = marketsContextHack;
  const investibleState = investibleContextHack;
  const commentsState = commentsContextHack;
  const ticketState = ticketContextHack;
  const urlParts = new URL(url);
  console.info("url", url);
  if (url.includes(OUR_CND_DOMAIN_ENDING)) {
    const attachmentPath = urlParts.pathname.split('/').slice(1).join('/');
    return attachmentPathHack[attachmentPath];
  } else if (isTicketPath(urlParts.pathname)) {
    const ticket = getTicket(ticketState, urlParts.pathname.substring(1));
    if (ticket) {
      if (isInvestibleTicket(urlParts.pathname)) {
        const { investibleId } = ticket;
        const name = getInvestibleName(investibleState, investibleId);
        if (!_.isEmpty(name)) {
          return name;
        }
      } else {
        const { marketId, commentId } = ticket;
        const rootComment = getCommentRoot(commentsState, marketId, commentId);
        if (rootComment) {
          return getNameForComment(rootComment, investibleState);
        }
      }
    }
  } else {
    const isComment = urlParts.hash && urlParts.hash.startsWith('#c') && !urlParts.hash.startsWith('#cv');
    if (urlParts.host === window.location.host && (!urlParts.hash || isComment)) {
      const { action, marketId, investibleId } = decomposeMarketPath(urlParts.pathname);
      if (action === 'dialog') {
        if (isComment) {
          const commentId = urlParts.hash.substring(2, urlParts.hash.length);
          const rootComment = getCommentRoot(commentsState, marketId, commentId);
          if (rootComment) {
            return getNameForComment(rootComment, investibleState);
          }
        }
        if (investibleId) {
          const name = getInvestibleName(investibleState, investibleId);
          if (!_.isEmpty(name)) {
            return name;
          }
        }
        if (marketId) {
          const market = getMarket(marketState, marketId);
          if (!_.isEmpty(market)) {
            const { name } = market;
            return name;
          }
        }
      }
    }
  }
  return undefined;
}

// T-all-1704: a link's display name comes from its root comment (see getNameForUrl),
// so a link whose root comment is resolved renders struck through in MyLink
// S-all-321: a short code link keeps the code as its text, so the target's current
// title is shown on hover instead of being copied into every body that mentions it.
// Resolution is the same walk isUrlToResolvedComment does for the strike through, and
// it is absent in the same cases: a target this session has not loaded has no name.
export function getLinkTargetName(url, ticketState = ticketContextHack,
  commentsState = commentsContextHack, investiblesState = investibleContextHack) {
  try {
    // stored link hrefs can be relative - resolve them against this host
    const urlParts = new URL(url, window.location.origin);
    if (isTicketPath(urlParts.pathname)) {
      const ticket = getTicket(ticketState, urlParts.pathname.substring(1));
      if (!ticket) {
        return undefined;
      }
      if (isInvestibleTicket(urlParts.pathname)) {
        return getInvestibleName(investiblesState, ticket.investibleId);
      }
      const comment = getComment(commentsState, ticket.marketId, ticket.commentId);
      return comment ? nameFromDescription(comment.body) : undefined;
    }
    if (urlParts.host !== window.location.host) {
      return undefined;
    }
    const { action, marketId, investibleId } = decomposeMarketPath(urlParts.pathname);
    if (action !== 'dialog') {
      return undefined;
    }
    // A comment anchors #c on its investible or its group; a job has no hash at all.
    if (urlParts.hash && urlParts.hash.startsWith('#c') && !urlParts.hash.startsWith('#cv')) {
      const commentId = urlParts.hash.substring(2);
      const comment = getComment(commentsState, marketId, commentId);
      return comment ? nameFromDescription(comment.body) : undefined;
    }
    if (investibleId) {
      return getInvestibleName(investiblesState, investibleId);
    }
  } catch (e) {
    return undefined;
  }
  return undefined;
}

export function isUrlToResolvedComment(url) {
  const commentsState = commentsContextHack;
  const ticketState = ticketContextHack;
  try {
    // stored link hrefs can be relative - resolve them against this host
    const urlParts = new URL(url, window.location.origin);
    if (isTicketPath(urlParts.pathname)) {
      if (isInvestibleTicket(urlParts.pathname)) {
        return false;
      }
      const ticket = getTicket(ticketState, urlParts.pathname.substring(1));
      if (ticket) {
        const { marketId, commentId } = ticket;
        const rootComment = getCommentRoot(commentsState, marketId, commentId);
        return !!rootComment?.resolved;
      }
    } else if (urlParts.host === window.location.host && urlParts.hash && urlParts.hash.startsWith('#c')
      && !urlParts.hash.startsWith('#cv')) {
      const { action, marketId } = decomposeMarketPath(urlParts.pathname);
      if (action === 'dialog') {
        const commentId = urlParts.hash.substring(2, urlParts.hash.length);
        const rootComment = getCommentRoot(commentsState, marketId, commentId);
        return !!rootComment?.resolved;
      }
    }
  } catch (e) {
    return false;
  }
  return false;
}

export function openInNewTab(url) {
  const win = window.open(url, '_blank');
  win.focus();
}

export function formCommentLink(marketId, groupId, investibleId, commentId) {
  const commentPart = `#c${commentId}`;
  if (!_.isEmpty(investibleId)) {
    return formInvestibleLink(marketId, investibleId) + commentPart;
  }
  return formMarketLink(marketId, groupId) + commentPart;
}

export function formArchiveCommentLink(marketId, groupId, commentId) {
  return formGroupArchiveLink(marketId, groupId) + `#c${commentId}`;
}

export function formInvestibleLinkWithPrefix(preFix, marketId, investibleId) {
  return `/${preFix}/${marketId}/${investibleId}`;
}

export function formInvestibleLink(marketId, investibleId) {
  return formInvestibleLinkWithPrefix('dialog', marketId, investibleId);
}

export function formatGroupLinkWithPrefix(prefix, marketId, groupId) {
  return `/${prefix}/${marketId}?groupId=${groupId}`;
}

export function formatGroupLinkWithSuffix(suffix, marketId, groupId) {
  return `/dialog/${marketId}?groupId=${groupId}#${suffix}`;
}

export function removeHash(history) {
  history.replace(history.location.pathname + history.location.search, history.location.state);
}

export function navigateToOption(history, parentMarketId, parentInvestibleId, groupId, id) {
  if (parentInvestibleId) {
    navigate(history, `${formInvestibleLink(parentMarketId, parentInvestibleId)}#option${id}`);
  } else if (parentMarketId) {
    navigate(history, `${formMarketLink(parentMarketId, groupId)}#option${id}`);
  }
}

/**
 * Forms a link to a given market id with the given subpath. Usually used when switching
 * to a different market
 * @param marketId
 * @param groupId
 * @returns {string}
 */
export function formMarketLink(marketId, groupId) {
  return formatGroupLinkWithPrefix('dialog', marketId, groupId);
}

export function formMarketEditLink(marketId) {
  return `/marketEdit/${marketId}`;
}

export function formManageUsersLink(marketId) {
  return `/manageUsers/${marketId}`;
}

export function formGroupEditLink(marketId, groupId) {
  return formatGroupLinkWithPrefix('groupEdit', marketId, groupId);
}

export function formGroupManageLink(marketId, groupId) {
  return formatGroupLinkWithPrefix('groupManage', marketId, groupId);
}

export function formGroupArchiveLink(marketId, groupId) {
  return formatGroupLinkWithPrefix('groupArchive', marketId, groupId);
}

export function formCompleteJobsLink(marketId, groupId, presenceId) {
  let link = formGroupArchiveLink(marketId, groupId);
  if (presenceId) {
    link += `&assigneeId=${presenceId}`;
  }
  return link;
}

export function formInvestibleAddCommentLink(wizardType, investibleId, marketId, commentType, typeObjectId, decisionInvestibleId, 
  decisionMarketId, notificationType) {
  let link = `/wizard#type=${wizardType.toLowerCase()}&investibleId=${investibleId}`;
  if (marketId) {
    link += `&marketId=${marketId}`;
  }
  if (commentType) {
    link += `&commentType=${commentType}`;
  }
  if (typeObjectId) {
    link += `&typeObjectId=${typeObjectId}`
  }
  if (decisionInvestibleId) {
    link += `&decisionInvestibleId=${decisionInvestibleId}&decisionMarketId=${decisionMarketId}`;
  }
  if (notificationType) {
    link += `&notificationType=${notificationType}`;
  }
  return link;
}

export function formMarketAddCommentLink(wizardType, marketId, groupId, commentType) {
  let link = `/wizard#type=${wizardType}&marketId=${marketId}&groupId=${groupId}`;
  if (commentType !== undefined) {
    link += `&commentType=${commentType}`;
  }
  return link;
}

export function formWizardLink(wizardType, marketId, investibleId, groupId, commentId, typeObjectId) {
  let link = `/wizard#type=${wizardType.toLowerCase()}&marketId=${marketId}`;
  if (investibleId) {
    link += `&investibleId=${investibleId}`;
  }
  if (groupId) {
    link += `&groupId=${groupId}`;
  }
  if (commentId) {
    link += `&commentId=${commentId}`;
  }
  if (typeObjectId) {
    link += `&typeObjectId=${typeObjectId}`
  }
  return link;

}

export function formMarketAddInvestibleLink(marketId, groupId, jobType, typeObjectId, wizardType=JOB_WIZARD_TYPE)
{
  let baseLink = `/wizard#type=${wizardType}&marketId=${marketId}&groupId=${groupId}`
  if (typeObjectId) {
    baseLink += `&typeObjectId=${typeObjectId}`
  }
  if (jobType !== undefined) {
    return `${baseLink}&jobType=${jobType}`;
  }
  return baseLink;
}
